import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, cn, CURRENCIES } from '../lib/utils';
import { getApiErrorMessage } from '../lib/apiError';
import { analytics } from '../lib/analytics';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Trash2, CalendarIcon, Loader2, ArrowLeft, Search, UserPlus, Check, X, Layout as LayoutIcon, Lock, Package, Eye } from 'lucide-react';

const CreateInvoice = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [template, setTemplate] = useState('brutalist');
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([
    { id: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0 }
  ]);

  // Client search state
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // New client dialog state
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    company: '',
    address: '',
    phone: ''
  });

  // Product state
  const [products, setProducts] = useState([]);
  const [productSearches, setProductSearches] = useState({}); // Track search per line item
  const [showProductDropdown, setShowProductDropdown] = useState({});
  
  // New product dialog state
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForItemIndex, setProductForItemIndex] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    unit_price: '',
    category: ''
  });
  
  // Payment terms state
  const [paymentTerms, setPaymentTerms] = useState('net_30');
  const [lateFeePercentage, setLateFeePercentage] = useState(0);

  // Template preview state
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchDefaultCurrency();
    fetchTemplates();
    fetchProducts();
  }, []);

  // Calculate due date based on payment terms
  useEffect(() => {
    const today = new Date();
    switch (paymentTerms) {
      case 'due_on_receipt':
        setDueDate(today);
        break;
      case 'net_15':
        setDueDate(new Date(today.getTime() + 15 * 24 * 60 * 60 * 1000));
        break;
      case 'net_30':
        setDueDate(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000));
        break;
      case 'net_60':
        setDueDate(new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000));
        break;
      case 'custom':
        // Keep existing due date for custom
        break;
      default:
        break;
    }
  }, [paymentTerms]);

  const fetchDefaultCurrency = async () => {
    try {
      const response = await api.get('/branding');
      const defaultCurrency = response.data?.branding?.default_currency || 'USD';
      setCurrency(defaultCurrency);
    } catch (error) {
      // Use USD as fallback
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data?.templates || []);
    } catch (error) {
      // Use default templates
      setTemplates([
        { id: 'brutalist', name: 'Brutalist', description: 'Oversized type, exposed grids, gallery-paper ivory. For work that belongs on a wall.', available: true, requires_upgrade: false, is_default: true },
        { id: 'classic', name: 'Classic', description: 'Clean, professional layout', available: true, requires_upgrade: false },
        { id: 'modern', name: 'Modern', description: 'Bold headers with vibrant accents', available: false, requires_upgrade: true },
        { id: 'minimal', name: 'Minimal', description: 'Simple, text-focused design', available: false, requires_upgrade: true },
        { id: 'aesthetic', name: 'Aesthetic', description: 'Modern art-inspired layout', available: false, requires_upgrade: true }
      ]);
    }
  };

  // Handle template preview
  const handlePreviewTemplate = async (templateId) => {
    setPreviewTemplate(templateId);
    setShowTemplatePreview(true);
    setPreviewLoading(true);
    setPreviewPdfUrl(null);
    
    try {
      const response = await api.get(`/templates/${templateId}/preview`, {
        params: { format: 'png' },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
      setPreviewPdfUrl(url);
    } catch (error) {
      toast.error('Failed to load template preview');
      setShowTemplatePreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleanup preview URL on unmount or when closing modal
  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        window.URL.revokeObjectURL(previewPdfUrl);
      }
    };
  }, [previewPdfUrl]);

  const closeTemplatePreview = () => {
    setShowTemplatePreview(false);
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
  };

  // Filter clients based on search
  useEffect(() => {
    if (clientSearch.trim() === '') {
      setFilteredClients(clients);
    } else {
      const searchLower = clientSearch.toLowerCase();
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(searchLower) ||
        (client.email && client.email.toLowerCase().includes(searchLower)) ||
        (client.company && client.company.toLowerCase().includes(searchLower))
      );
      setFilteredClients(filtered);
    }
  }, [clientSearch, clients]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data.products || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to fetch products:', error);
    }
  };

  const getFilteredProducts = (search) => {
    if (!search || search.trim() === '') return products;
    const searchLower = search.toLowerCase();
    return products.filter(p => 
      p.name.toLowerCase().includes(searchLower) ||
      p.description?.toLowerCase().includes(searchLower) ||
      p.category?.toLowerCase().includes(searchLower)
    );
  };

  const selectProduct = (index, product) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      description: product.name + (product.description ? ` - ${product.description}` : ''),
      unit_price: product.unit_price
    };
    setItems(newItems);
    setShowProductDropdown({ ...showProductDropdown, [index]: false });
    setProductSearches({ ...productSearches, [index]: '' });
  };

  const handleProductSearch = (index, value) => {
    setProductSearches({ ...productSearches, [index]: value });
    setShowProductDropdown({ ...showProductDropdown, [index]: true });
  };

  const openNewProductDialog = (index) => {
    setProductForItemIndex(index);
    setNewProduct({
      name: productSearches[index] || '',
      description: '',
      unit_price: '',
      category: ''
    });
    setShowNewProductDialog(true);
    setShowProductDropdown({ ...showProductDropdown, [index]: false });
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!newProduct.unit_price || parseFloat(newProduct.unit_price) < 0) {
      toast.error('Valid price is required');
      return;
    }

    setSavingProduct(true);
    try {
      const response = await api.post('/products', {
        name: newProduct.name.trim(),
        description: newProduct.description.trim() || null,
        unit_price: parseFloat(newProduct.unit_price),
        category: newProduct.category.trim() || null,
        tags: []
      });

      toast.success('Product created');
      
      // Refresh products list
      await fetchProducts();
      
      // Auto-select the new product for the line item
      if (productForItemIndex !== null) {
        selectProduct(productForItemIndex, response.data);
      }
      
      setShowNewProductDialog(false);
      setNewProduct({ name: '', description: '', unit_price: '', category: '' });
      setProductForItemIndex(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create product'));
    } finally {
      setSavingProduct(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch(client.name);
    setShowClientDropdown(false);
  };

  const handleClientSearchFocus = () => {
    setShowClientDropdown(true);
  };

  const handleClientSearchChange = (e) => {
    setClientSearch(e.target.value);
    setSelectedClient(null); // Clear selection when typing
    setShowClientDropdown(true);
  };

  const clearClientSelection = () => {
    setSelectedClient(null);
    setClientSearch('');
  };

  const openNewClientDialog = () => {
    // Pre-fill name with search term if it doesn't match any client
    setNewClient({
      name: clientSearch,
      email: '',
      company: '',
      address: '',
      phone: ''
    });
    setShowNewClientDialog(true);
    setShowClientDropdown(false);
  };

  const handleCreateClient = async () => {
    if (!newClient.name) {
      toast.error('Name is required');
      return;
    }
    if (!newClient.email && !newClient.phone) {
      toast.error('A client needs at least an email address or a phone number');
      return;
    }

    setSavingClient(true);
    try {
      const response = await api.post('/clients', {
        name: newClient.name.trim(),
        email: newClient.email.trim() || null,
        company: newClient.company.trim() || null,
        address: newClient.address.trim() || null,
        phone: newClient.phone.trim() || null
      });
      const createdClient = response.data;
      
      // Add to clients list and select it
      setClients(prev => [...prev, createdClient]);
      setSelectedClient(createdClient);
      setClientSearch(createdClient.name);
      setShowNewClientDialog(false);
      setNewClient({ name: '', email: '', company: '', address: '', phone: '' });
      toast.success('Client created successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create client'));
    } finally {
      setSavingClient(false);
    }
  };

  const addItem = () => {
    setItems([...items, { id: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setItems(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    if (!dueDate) {
      toast.error('Please select a due date');
      return;
    }
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all item details');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/invoices', {
        client_id: selectedClient.client_id,
        items: items.map(({ id, ...item }) => item),
        due_date: format(dueDate, 'yyyy-MM-dd'),
        notes: notes || null,
        tax_rate: taxRate,
        currency: currency,
        template: template,
        payment_terms: paymentTerms,
        late_fee_percentage: lateFeePercentage
      });
      toast.success('Invoice created successfully');
      analytics.invoiceCreated(calculateTotal(), currency);
      // Redirect to invoice detail page so user can send/download immediately
      navigate(`/invoices/${response.data.invoice_id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create invoice'));
    } finally {
      setLoading(false);
    }
  };

  const planLimits = {
    free: { limit: 2, name: 'Free' },
    standard: { limit: 5, name: 'Standard' },
    pro: { limit: -1, name: 'Pro' }
  };
  const currentPlan = planLimits[user?.subscription_plan || 'free'];
  const invoicesUsed = user?.invoices_this_month || 0;
  const canCreateInvoice = currentPlan.limit === -1 || invoicesUsed < currentPlan.limit;

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-4xl mx-auto space-y-8" data-testid="create-invoice-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">Create Invoice</h1>
            <p className="text-muted-foreground mt-1">Fill in the details to create a new invoice</p>
          </div>
        </div>

        {!canCreateInvoice && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="p-4">
              <p className="text-destructive font-medium">
                You&apos;ve reached your invoice limit for this month ({invoicesUsed}/{currentPlan.limit}).
                <Button variant="link" className="text-destructive px-1" onClick={() => navigate('/settings')}>
                  Upgrade your plan
                </Button>
                to create more invoices.
              </p>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Selection with Search */}
          <Card>
            <CardHeader>
              <CardTitle>Client Details</CardTitle>
              <CardDescription>Search for an existing client or add a new one</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Label htmlFor="client-search">Client</Label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    ref={searchRef}
                    id="client-search"
                    placeholder="Search clients by name, email, or company..."
                    value={clientSearch}
                    onChange={handleClientSearchChange}
                    onFocus={handleClientSearchFocus}
                    className={cn(
                      "pl-10 pr-10",
                      selectedClient && "border-primary"
                    )}
                    data-testid="client-search-input"
                  />
                  {selectedClient && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={clearClientSelection}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Client dropdown */}
                {showClientDropdown && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-64 overflow-auto"
                  >
                    {filteredClients.length > 0 ? (
                      <>
                        {filteredClients.map((client) => (
                          <button
                            key={client.client_id}
                            type="button"
                            className={cn(
                              "w-full px-4 py-3 text-left hover:bg-accent flex items-center justify-between transition-colors",
                              selectedClient?.client_id === client.client_id && "bg-accent"
                            )}
                            onClick={() => handleClientSelect(client)}
                            data-testid={`client-option-${client.client_id}`}
                          >
                            <div>
                              <p className="font-medium">{client.name}</p>
                              <p className="text-sm text-muted-foreground">{client.email}</p>
                              {client.company && (
                                <p className="text-xs text-muted-foreground">{client.company}</p>
                              )}
                            </div>
                            {selectedClient?.client_id === client.client_id && (
                              <Check className="w-4 h-4 text-primary" />
                            )}
                          </button>
                        ))}
                      </>
                    ) : clientSearch.trim() !== '' ? (
                      <div className="p-4 text-center">
                        <p className="text-muted-foreground mb-3">No clients found matching &quot;{clientSearch}&quot;</p>
                      </div>
                    ) : (
                      <div className="p-4 text-center text-muted-foreground">
                        Start typing to search clients
                      </div>
                    )}
                    
                    {/* Add new client button */}
                    <div className="border-t p-2">
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full justify-start gap-2 text-primary hover:text-primary"
                        onClick={openNewClientDialog}
                        data-testid="add-new-client-btn"
                      >
                        <UserPlus className="w-4 h-4" />
                        Add new client{clientSearch && ` "${clientSearch}"`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Selected client preview */}
              {selectedClient && (
                <div className="p-4 bg-muted rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-muted-foreground">Selected Client</p>
                      <p className="font-semibold">{selectedClient.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedClient.email}</p>
                      {selectedClient.company && (
                        <p className="text-sm text-muted-foreground">{selectedClient.company}</p>
                      )}
                    </div>
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
              <CardDescription>Add the items or services to invoice</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Currency Selector */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <Label className="whitespace-nowrap">Invoice Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-48" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCIES).map(([code, info]) => (
                      <SelectItem key={code} value={code}>
                        {info.symbol} {code} - {info.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {items.map((item, index) => (
                <div key={item.id} className="space-y-2 p-4 border rounded-lg" data-testid={`line-item-${index}`}>
                  {/* Product Search Row */}
                  <div className="relative">
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      Quick Add from Products
                    </Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search saved products or type to add new..."
                        value={productSearches[index] || ''}
                        onChange={(e) => handleProductSearch(index, e.target.value)}
                        onFocus={() => setShowProductDropdown({ ...showProductDropdown, [index]: true })}
                        className="pl-9 text-sm"
                        data-testid={`product-search-${index}`}
                      />
                    </div>
                    
                    {/* Product Dropdown */}
                    {showProductDropdown[index] && (
                      <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
                        {getFilteredProducts(productSearches[index]).length > 0 ? (
                          <>
                            {getFilteredProducts(productSearches[index]).map((product) => (
                              <button
                                key={product.product_id}
                                type="button"
                                className="w-full px-3 py-2 text-left hover:bg-accent flex items-center justify-between"
                                onClick={() => selectProduct(index, product)}
                              >
                                <div>
                                  <p className="font-medium text-sm">{product.name}</p>
                                  {product.category && (
                                    <p className="text-xs text-muted-foreground">{product.category}</p>
                                  )}
                                </div>
                                <span className="text-sm font-mono text-muted-foreground">
                                  ${product.unit_price.toFixed(2)}
                                </span>
                              </button>
                            ))}
                          </>
                        ) : products.length === 0 ? (
                          <div className="px-3 py-4 text-center">
                            <p className="text-sm text-muted-foreground mb-2">No saved products yet</p>
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            No products match your search
                          </div>
                        )}
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-accent border-t flex items-center gap-2 text-primary"
                          onClick={() => openNewProductDialog(index)}
                        >
                          <Plus className="w-4 h-4" />
                          <span className="text-sm font-medium">Create new product</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Item Details Row */}
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <Label>Description</Label>
                      <Input
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="w-24">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="w-32">
                      <Label>Unit Price ({CURRENCIES[currency]?.symbol || '$'})</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div className="w-32 text-right">
                      <Label className="invisible">Total</Label>
                      <p className="font-mono font-medium py-2">
                        {formatCurrency(item.quantity * item.unit_price, currency)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" onClick={addItem} className="gap-2" data-testid="add-item-btn">
                <Plus className="w-4 h-4" />
                Add Item
              </Button>

              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-medium">{formatCurrency(calculateSubtotal(), currency)}</span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="tax-rate" className="text-sm text-muted-foreground whitespace-nowrap">Tax Rate</Label>
                    <div className="flex items-center">
                      <Input
                        id="tax-rate"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={taxRate}
                        onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                        className="w-20 text-right"
                        data-testid="tax-rate-input"
                      />
                      <span className="ml-1 text-muted-foreground">%</span>
                    </div>
                  </div>
                  <span className="font-mono">{formatCurrency(calculateTax(), currency)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-medium">Total</span>
                  <span className="text-2xl font-heading font-bold">{formatCurrency(calculateTotal(), currency)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payment Terms */}
              <div>
                <Label>Payment Terms</Label>
                <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                  <SelectTrigger data-testid="payment-terms-select">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                    <SelectItem value="net_15">Net 15 (15 days)</SelectItem>
                    <SelectItem value="net_30">Net 30 (30 days)</SelectItem>
                    <SelectItem value="net_60">Net 60 (60 days)</SelectItem>
                    <SelectItem value="custom">Custom Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !dueDate && "text-muted-foreground"
                      )}
                      data-testid="due-date-btn"
                      disabled={paymentTerms !== 'custom'}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, "PPP") : "Select due date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {paymentTerms !== 'custom' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Due date is automatically calculated based on payment terms
                  </p>
                )}
              </div>

              {/* Late Fee */}
              <div>
                <Label htmlFor="lateFee">Late Fee (% per month)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="lateFee"
                    type="number"
                    min="0"
                    max="25"
                    step="0.5"
                    value={lateFeePercentage}
                    onChange={(e) => setLateFeePercentage(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="w-24"
                    data-testid="late-fee-input"
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {lateFeePercentage > 0 
                    ? `${lateFeePercentage}% will be applied monthly on overdue invoices`
                    : 'Set to 0 for no late fees'}
                </p>
              </div>

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes for your client..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Invoice Template Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <LayoutIcon className="w-4 h-4" />
                  Invoice Template
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      className={cn(
                        "relative rounded-lg border-2 transition-all overflow-hidden",
                        template === t.id 
                          ? "border-primary bg-primary/5" 
                          : t.available 
                            ? "border-border hover:border-primary/50" 
                            : "border-border bg-muted/30"
                      )}
                      data-testid={`template-card-${t.id}`}
                    >
                      {/* Card header with selection */}
                      <button
                        type="button"
                        onClick={() => t.available && setTemplate(t.id)}
                        disabled={!t.available}
                        className={cn(
                          "w-full p-4 text-left",
                          !t.available && "cursor-not-allowed opacity-70"
                        )}
                        data-testid={`template-${t.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              {t.name}
                              {t.is_default && (
                                <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">Default</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                          </div>
                          {template === t.id && t.available && (
                            <Check className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                          )}
                          {!t.available && (
                            <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0 ml-2" />
                          )}
                        </div>
                        {t.requires_upgrade && (
                          <Badge variant="outline" className="mt-2 text-xs">Standard Plan</Badge>
                        )}
                      </button>
                      
                      {/* Preview button - always visible and clickable */}
                      <div className="px-4 pb-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewTemplate(t.id);
                          }}
                          className="w-full gap-2"
                          data-testid={`preview-template-${t.id}`}
                        >
                          <Eye className="w-3 h-3" />
                          Preview
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/invoices')} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !canCreateInvoice} data-testid="create-invoice-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Invoice'}
            </Button>
          </div>
        </form>
      </div>

      {/* New Client Dialog */}
      <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Create a new client to add to your invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="new-client-name">Name *</Label>
                <Input
                  id="new-client-name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="John Doe"
                  data-testid="new-client-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-client-email">Email</Label>
                <Input
                  id="new-client-email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="new-client-email-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Required unless phone is provided</p>
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-client-company">Company</Label>
                <Input
                  id="new-client-company"
                  value={newClient.company}
                  onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                  placeholder="Acme Inc."
                  data-testid="new-client-company-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-client-phone">Phone</Label>
                <Input
                  id="new-client-phone"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  data-testid="new-client-phone-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Required unless email is provided</p>
              </div>
              <div className="col-span-2">
                <Label htmlFor="new-client-address">Address</Label>
                <Input
                  id="new-client-address"
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  placeholder="123 Main St, City, Country"
                  data-testid="new-client-address-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowNewClientDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateClient} disabled={savingClient} data-testid="save-new-client-btn">
              {savingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Product Dialog */}
      <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Product</DialogTitle>
            <DialogDescription>
              Add a new product to your catalog. It will be saved for future invoices.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product-name">Product Name *</Label>
              <Input
                id="product-name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder="e.g., Website Design"
                data-testid="new-product-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-description">Description</Label>
              <Input
                id="product-description"
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder="Brief description"
                data-testid="new-product-description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-price">Unit Price *</Label>
              <Input
                id="product-price"
                type="number"
                min="0"
                step="0.01"
                value={newProduct.unit_price}
                onChange={(e) => setNewProduct({ ...newProduct, unit_price: e.target.value })}
                placeholder="0.00"
                data-testid="new-product-price"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-category">Category</Label>
              <Input
                id="product-category"
                value={newProduct.category}
                onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                placeholder="e.g., Design, Development"
                data-testid="new-product-category"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProductDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProduct} disabled={savingProduct} data-testid="save-new-product-btn">
              {savingProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create & Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={showTemplatePreview} onOpenChange={closeTemplatePreview}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0" data-testid="template-preview-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {previewTemplate && templates.find(t => t.id === previewTemplate)?.name} Template Preview
            </DialogTitle>
            <DialogDescription>
              This is a sample invoice showing how this template looks with your branding
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
                  <p className="text-sm text-muted-foreground">Loading preview...</p>
                </div>
              </div>
            ) : previewPdfUrl ? (
              <img
                src={previewPdfUrl}
                alt="Template Preview"
                className="w-full object-contain"
                data-testid="template-preview-image"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Failed to load preview</p>
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t flex items-center justify-between">
            {previewTemplate && templates.find(t => t.id === previewTemplate)?.requires_upgrade ? (
              <p className="text-sm text-muted-foreground">
                <Lock className="w-3 h-3 inline mr-1" />
                Upgrade to Standard plan to use this template
              </p>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeTemplatePreview}>
                Close
              </Button>
              {previewTemplate && templates.find(t => t.id === previewTemplate)?.available && (
                <Button 
                  onClick={() => {
                    setTemplate(previewTemplate);
                    closeTemplatePreview();
                    toast.success(`${templates.find(t => t.id === previewTemplate)?.name} template selected`);
                  }}
                  data-testid="select-template-from-preview"
                >
                  Use This Template
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CreateInvoice;
